import { useEffect, useRef } from 'react';
import { useMindMapStore } from '../store/useMindMapStore';
import { auth, db, handleFirestoreError, OperationType, getEnvVar } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp, getDocFromServer, query, collection, where, orderBy, limit, getDocs, onSnapshot } from 'firebase/firestore';
import { INITIAL_MARKDOWN, PALETTES } from '../constants';
import { convertTreeToMarkdown } from '../utils/markdownParser';
import { MindMapNode } from '../types';

export const useFirebaseSync = (
  handleFileUpload: (content: string, name: string, force?: boolean, initialHistory?: MindMapNode[], initialDirty?: boolean, mindmapId?: string | null) => void,
  getViewState?: () => { x: number; y: number; k: number } | undefined
) => {
  const store = useMindMapStore();
  const sessionId = useRef<string>(`session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const currentMindmapIdRef = useRef<string | null>(null);

  useEffect(() => { currentMindmapIdRef.current = store.currentMindmapId; }, [store.currentMindmapId]);

  useEffect(() => {
    async function testConnection() {
      try {
        await getDocFromServer(doc(db, 'test', 'connection'));
      } catch (error) {
        if(error instanceof Error && error.message.includes('the client is offline')) {
          console.error("Please check your Firebase configuration. ");
        }
      }
    }
    testConnection();
  }, []);

  useEffect(() => {
    const isDebug = new URLSearchParams(window.location.search).get('debug') === 'true';
    if (isDebug) {
      store.setUserId('debug-user-123');
      store.setIsAccessDenied(false);
      store.setUserName('Debug User');
      store.setIsAuthLoading(false);
      
      const draftJson = localStorage.getItem('mindmap_draft');
      let loadedDraft = false;
      if (draftJson) {
        try {
          const draft = JSON.parse(draftJson);
          if (draft.isDirty && draft.rawMarkdown) {
            handleFileUpload(draft.rawMarkdown, draft.fileName, false, draft.history, draft.isDirty);
            loadedDraft = true;
          }
        } catch (e) {
          console.error("Failed to parse local draft", e);
        }
      }
      
      if (!loadedDraft) {
        handleFileUpload(INITIAL_MARKDOWN, "Mindmap demo.md", true);
      }
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const allowedUsers = (getEnvVar('VITE_ALLOWED_USERS') || '')
          .split(',')
          .map(u => u.trim().toLowerCase())
          .filter(u => u !== '');
        const userEmail = user.email?.toLowerCase() || '';

        if (allowedUsers.length > 0 && !allowedUsers.includes(userEmail)) {
          console.warn(`Access denied for ${userEmail}. Only allowed users are: ${allowedUsers.join(', ')}`);
          store.setUserId(null);
          store.setUserName(null);
          store.setIsAccessDenied(true);
          store.setIsAuthLoading(false);
          return;
        }

        store.setUserId(user.uid);
        store.setIsAccessDenied(false);
        store.setUserName(user.displayName || user.email || 'User');
        
        const draftJson = localStorage.getItem('mindmap_draft');
        let loadedDraft = false;
        if (draftJson) {
          try {
            const draft = JSON.parse(draftJson);
            if (draft.isDirty && draft.rawMarkdown) {
              handleFileUpload(draft.rawMarkdown, draft.fileName, false, draft.history, draft.isDirty);
              loadedDraft = true;
            }
          } catch (e) {
            console.error("Failed to parse local draft", e);
          }
        }

        const fetchSettings = async () => {
          try {
            const docRef = doc(db, 'users', user.uid);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
              const data = docSnap.data();
              if (data.isDarkMode !== undefined) store.setIsDarkMode(data.isDarkMode);
              if (data.language !== undefined) store.setLanguage(data.language);
              if (data.showEmoji !== undefined) store.setShowEmoji(data.showEmoji);
              if (data.isMandalaMode !== undefined) store.setIsMandalaMode(data.isMandalaMode);
              if (data.paletteId) {
                const foundPalette = PALETTES.find(p => p.name === data.paletteId);
                if (foundPalette) store.setPalette(foundPalette);
              }
            }
          } catch (err) {
            console.warn("Could not load user settings:", err);
          }
        };

        const fetchMindmap = async () => {
          if (loadedDraft) return;
          try {
            const q = query(
              collection(db, 'mindmaps'),
              where('userId', '==', user.uid),
              orderBy('updatedAt', 'desc'),
              limit(1)
            );
            const querySnapshot = await getDocs(q);
            if (!querySnapshot.empty) {
              const lastMap = querySnapshot.docs[0].data();
              const mapId = querySnapshot.docs[0].id;
              let restoredHistory: MindMapNode[] | undefined = undefined;
              if (lastMap.history && Array.isArray(lastMap.history)) {
                 restoredHistory = lastMap.history;
              }
              handleFileUpload(lastMap.rawMarkdown, lastMap.title + '.md', true, restoredHistory, false, mapId);
            } else {
              handleFileUpload(INITIAL_MARKDOWN, "Mindmap demo.md", true, undefined, false, null);
            }
          } catch (err) {
            console.warn("Could not fetch user mindmaps from Firestore:", err);
            handleFileUpload(INITIAL_MARKDOWN, "Mindmap demo.md", true);
          }
        };

        Promise.all([fetchSettings(), fetchMindmap()]).then(() => {
          store.setIsAuthLoading(false);
        });
      } else {
        const draftJson = localStorage.getItem('mindmap_draft');
        let loadedDraft = false;
        if (draftJson) {
          try {
            const draft = JSON.parse(draftJson);
            if (draft.isDirty && draft.rawMarkdown) {
              handleFileUpload(draft.rawMarkdown, draft.fileName, false, draft.history, draft.isDirty);
              loadedDraft = true;
            }
          } catch (e) {
            console.error("Failed to parse local draft", e);
          }
        }
        store.setUserId(null);
        store.setUserName(null);
        store.setIsAccessDenied(false);
        store.setIsAuthLoading(false);
        if (!loadedDraft) {
          handleFileUpload(INITIAL_MARKDOWN, "Mindmap demo.md", true);
        }
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (store.userId) {
      const saveSettings = async () => {
        try {
          await setDoc(doc(db, 'users', store.userId as string), {
            isDarkMode: store.isDarkMode,
            language: store.language,
            showEmoji: store.showEmoji,
            isMandalaMode: store.isMandalaMode,
            paletteId: store.palette.name,
            updatedAt: serverTimestamp()
          });
        } catch (err) {
          try {
            handleFirestoreError(err, OperationType.UPDATE, `users/${store.userId}`);
          } catch (e) {
            console.error(e);
          }
        }
      };
      saveSettings();
    }
  }, [store.userId, store.isDarkMode, store.language, store.showEmoji, store.isMandalaMode, store.palette.name]);

  useEffect(() => {
    if (store.isDirty && store.data) {
      const currentMarkdown = convertTreeToMarkdown(store.data, store.palette.name, getViewState ? getViewState() : undefined, store.history);
      
      // Warn if the file size is getting close to the 1MB limit (800KB warning threshold)
      store.setFileSizeWarning(currentMarkdown.length > 800000);

      const draft = {
        rawMarkdown: currentMarkdown,
        fileName: store.fileName,
        isDirty: store.isDirty,
        timestamp: Date.now(),
        history: store.history
      };
      localStorage.setItem('mindmap_draft', JSON.stringify(draft));

      if (store.userId) {
        store.setSyncStatus('unsaved');
        const saveToCloud = async () => {
          try {
            const existingId = currentMindmapIdRef.current;
            const isNew = !existingId;
            const docId = existingId || `map_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            if (isNew) {
              currentMindmapIdRef.current = docId;
              store.setCurrentMindmapId(docId);
            }
            
            const docRef = doc(db, 'mindmaps', docId);
            store.setSyncStatus('saving');
            
            const cleanFileName = store.fileName.replace(/\.(md|txt)$/i, '');
            const docTitle = cleanFileName || store.data?.name?.trim() || 'Untitled Mindmap';

            await setDoc(docRef, {
              userId: store.userId,
              title: docTitle,
              rawMarkdown: currentMarkdown,
              updatedAt: serverTimestamp(),
              sessionId: sessionId.current,
              ...(isNew ? { createdAt: serverTimestamp() } : {})
            }, { merge: true });
            
            store.setSyncStatus('synced');
            store.setIsDirty(false);
          } catch (err) {
            console.error("[AutoSync] Auto-save failed:", err);
            store.setSyncStatus('error');
          }
        };

        const timeoutId = setTimeout(saveToCloud, 500);
        return () => clearTimeout(timeoutId);
      } else {
        store.setSyncStatus('unsaved');
      }
    } else if (!store.isDirty) {
      localStorage.removeItem('mindmap_draft');
    }
  }, [store.data, store.fileName, store.isDirty, store.history, store.userId, store.currentMindmapId, store.palette.name]);

  useEffect(() => {
    if (!store.currentMindmapId || !store.userId) return;

    const docRef = doc(db, 'mindmaps', store.currentMindmapId);
    const unsubscribe = onSnapshot(docRef, (snapshot) => {
      if (!snapshot.exists()) return;
      const data = snapshot.data();
      if (data.sessionId === sessionId.current) return;
      // if (!store.isDirty) return;
      // User requested to prioritize local edits and ignore false positives from other devices.
      // store.setRemoteConflict({
      //   rawMarkdown: data.rawMarkdown,
      //   title: data.title,
      //   updatedAt: data.updatedAt,
      // });
    }, (err) => {
      console.warn('onSnapshot error:', err);
    });

    return () => unsubscribe();
  }, [store.currentMindmapId, store.userId, store.isDirty]);

  return { sessionId: sessionId.current };
};
